import { Stack, Text, vars } from '@etg/wings';
import { Overlay } from '@eti/components';
import { TransferType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { DYNAMIC_ICON } from '../../common/utils/dynamic-icons/icons';
import { useDynamicIcon } from '../../common/utils/dynamic-icons/useDynamicIcon';
import ProductDrawerCard from '../../common/components/ProductDrawerCard';
import { AirportDirection, convertToHoursAndMinutes, findTransferBound, getDrawerValidityTextForBound, getValidityKind, getValidityKindForBound, ValidityKind, withoutNulls } from './utils';
import TransferBoundAvailability from './TransferBoundAvailability';
import TicketDetailsSection from './TicketDetailsSection';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const withIcon = iconContent => css`
  color: ${vars.colors.links.default};
  position: relative;

  &::after {
    bottom: 4px;
    content: ${iconContent};
    display: inline-block;
    height: 16px;
    margin-inline-start: 4px;
    position: relative;
    vertical-align: bottom;
    width: 16px;
  }
`;
const renderBounds = (operator, translation, showDepartureTime) => {
  const {
    available,
    departs: departsText,
    from: fromText,
    fromAirport: fromTitle,
    to: toText,
    toAirport: toTitle
  } = translation;
  const boundDirections = operator.airportDirection === AirportDirection.Both ? [AirportDirection.From, AirportDirection.To] : [operator.airportDirection];
  return /*#__PURE__*/_jsx(Stack, {
    children: boundDirections.map((direction, index) => {
      const title = direction === AirportDirection.From ? fromTitle : toTitle;
      return /*#__PURE__*/_jsx(TransferBoundAvailability, {
        available: available,
        boundsData: operator.transferBoundData,
        departsText: departsText,
        direction: direction,
        fromText: fromText,
        showDepartureTime: showDepartureTime,
        title: title,
        toText: toText
      }, index);
    })
  });
};
const getOfferedBounds = operator => {
  const directions = operator.airportDirection === AirportDirection.Both ? [AirportDirection.From, AirportDirection.To] : [operator.airportDirection];
  return directions.map(direction => findTransferBound(operator.transferBoundData, direction)).filter(withoutNulls());
};
const getBoundValidityBullet = (bound, translation) => {
  switch (getValidityKindForBound(bound)) {
    case ValidityKind.Open:
      return getDrawerValidityTextForBound(bound, translation);
    case ValidityKind.TimeSpecific:
      return translation.validityBookedSlotBullet;
    case ValidityKind.Fallback:
      return translation.validityInEmailBullet;
  }
};
const getValidityCopy = (operator, translation) => {
  const validityBullets = Array.from(new Set(getOfferedBounds(operator).map(bound => getBoundValidityBullet(bound, translation)).filter(withoutNulls())));
  const operatorKind = getValidityKind(operator);
  switch (operatorKind) {
    case ValidityKind.Open:
      return {
        validityBullets,
        operatingHours: translation.operatingHoursContent
      };
    case ValidityKind.TimeSpecific:
      return {
        validityBullets,
        operatingHours: null
      };
    case ValidityKind.Fallback:
      return {
        validityBullets,
        operatingHours: translation.operatingHoursFallbackContent
      };
  }
};
const AirportTransferDrawer = ({
  translation,
  operator,
  handleClose
}) => {
  var _findTransferBound;
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  if (!operator || operator.transferBoundData.length === 0 || operator.airportDirection === AirportDirection.None) {
    return null;
  }
  const {
    transferCarrier,
    transferTime,
    transferBoundData,
    transferType
  } = operator;
  const {
    andTranslation,
    availableTickets,
    bookingFinalBullet,
    bus,
    conditions,
    conditionsContent,
    hour,
    hours,
    minute,
    minutes,
    operatingHours,
    pleaseNote,
    pleaseNoteContent,
    termsAndConditions,
    ticketDetails,
    train,
    transferBetween,
    transferTime: transferTranslation
  } = translation;
  const transferTypeText = transferType === TransferType.Bus ? bus : train;
  const detailBound = (_findTransferBound = findTransferBound(transferBoundData, AirportDirection.From)) !== null && _findTransferBound !== void 0 ? _findTransferBound : findTransferBound(transferBoundData, AirportDirection.To);
  const arrival = detailBound === null || detailBound === void 0 ? void 0 : detailBound.arrivalStation;
  const departure = detailBound === null || detailBound === void 0 ? void 0 : detailBound.departureStation;
  const detailDirection = `${transferTypeText} ${transferBetween} ${departure} ${andTranslation} ${arrival}`;
  const termsAndConditionsUrl = detailBound === null || detailBound === void 0 ? void 0 : detailBound.termsAndConditions;
  const transferTimeText = convertToHoursAndMinutes(transferTime !== null && transferTime !== void 0 ? transferTime : 0, hour, hours, minute, minutes, transferTranslation);
  const isTimeSpecific = getValidityKind(operator) === ValidityKind.TimeSpecific;
  const {
    validityBullets,
    operatingHours: operatingHoursContent
  } = getValidityCopy(operator, translation);
  const ticketDetailsBullets = [detailDirection, transferTimeText, ...validityBullets].filter(withoutNulls());
  return /*#__PURE__*/_jsxs(Overlay.Drawer, {
    dataTestId: "airport-transfer-drawer",
    handleClose: handleClose,
    id: "airportTransfer-info-drawer",
    isOpen: Boolean(operator),
    children: [/*#__PURE__*/_jsx(Overlay.Header, {
      onClose: handleClose,
      title: transferCarrier
    }), /*#__PURE__*/_jsxs(Overlay.Content, {
      children: [/*#__PURE__*/_jsx(ProductDrawerCard, {
        cardTitle: availableTickets,
        children: renderBounds(operator, translation, isTimeSpecific)
      }), /*#__PURE__*/_jsx(ProductDrawerCard, {
        cardTitle: ticketDetails,
        children: /*#__PURE__*/_jsxs(Stack, {
          children: [/*#__PURE__*/_jsx(TicketDetailsSection, {
            content: ticketDetailsBullets
          }), operatingHoursContent && /*#__PURE__*/_jsx(TicketDetailsSection, {
            content: operatingHoursContent,
            title: operatingHours
          }), /*#__PURE__*/_jsx(TicketDetailsSection, {
            content: conditionsContent,
            title: conditions
          }), isTimeSpecific && bookingFinalBullet && /*#__PURE__*/_jsx(TicketDetailsSection, {
            content: bookingFinalBullet
          }), /*#__PURE__*/_jsx(TicketDetailsSection, {
            content: pleaseNoteContent,
            title: pleaseNote
          }), termsAndConditionsUrl && /*#__PURE__*/_jsx(Text, {
            as: "a",
            className: withIcon(icon),
            "data-testid": "airport-transfer-drawer-link",
            href: termsAndConditionsUrl,
            rel: "noreferrer",
            target: "_blank",
            children: termsAndConditions
          })]
        })
      })]
    })]
  });
};
export default AirportTransferDrawer;
//# sourceMappingURL=AirportTransferDrawer.js.map